"use client";

import { showWorkflowSessionSummaryToast } from "@/app/_components/adminWorkflows/showWorkflowSessionSummaryToast";
import { skipNextWorkflowBeforeUnload } from "@/app/_components/adminWorkflows/navigationUnloadGuard";
import type {
  RelayedStopWorkflowPayload,
  StopWorkflowResult,
} from "@/app/_components/adminWorkflows/autodialerSessionChannel";
import { workflowReducer } from "@/app/_components/adminWorkflows/workflowReducer";
import {
  isWorkflowStopConflict,
  reconcileWorkflowStopConflict,
} from "@/app/_components/adminWorkflows/workflow-stop/utils";
import { useIsMobile } from "@/app/_components/hooks/use-mobile";
import { trackDataDogAction } from "@/lib/analytics/trackDataDogAction";
import getLogger from "@/lib/logger";
import {
  fetchWorkflowSessionSummary,
  pauseWorkflow as pauseWorkflowAction,
  resumeWorkflowSession as resumeWorkflowSessionAction,
  startWorkflow as startWorkflowAction,
  syncWorkflowSession,
} from "@/lib/modules/calls/workflow/actions";
import {
  WORKFLOW_SYNC_SAVE_CONFLICT_ERROR,
  WORKFLOW_ALREADY_RUNNING_ERROR,
  WORKFLOW_REDIS_LEGACY_AUTO_ADVANCE,
  WORKFLOW_REDIS_LEGACY_DAILY_GOAL,
  WORKFLOW_INACTIVITY_CHECK_INTERVAL_MS,
  WORKFLOW_INACTIVITY_TIMEOUT_MS,
} from "@/lib/modules/calls/workflow/constants";
import type {
  CallWorkflowState,
  StopWorkflowOptions,
  WorkflowAction,
  WorkflowQueueItem,
  WorkflowRedisSessionInput,
  WorkflowReducerState,
  WorkflowResumeResult,
  WorkflowSessionSummaryStats,
} from "@/lib/modules/calls/workflow/types";
import {
  findCallableQueueTaskById,
  findDueScheduledCall,
  findNextNonSkippedQueueTask,
  findQueueTaskIndex,
  getInitialWorkflowQueueCursor,
  getSkippedTaskIdsFromQueue,
  getStudentIdFromAppPath,
  getWorkflowDateBoundaries,
  getWorkflowQueueBoundaryRefreshDelayMs,
  buildWorkflowSessionSummaryStats,
  getWorkflowQueueItemStudentId,
  getWorkflowTaskStudentMessagesPath,
  isCompactWorkflowViewport,
  resolveActiveWorkflowTaskId,
  resolveRestoredWorkflowCurrentTaskId,
  shouldHoldSilentRestoreNavigation,
  WORKFLOW_SESSION_SYNC_INTERVAL_MS,
  WORKFLOW_QUEUE_POLL_INTERVAL_MS,
  workflowSessionSummaryStatsToAnalyticsContext,
  resplitWorkflowQueueClientSide,
} from "@/lib/modules/calls/workflow/utils";
import {
  skipUserTask,
  unskipUserTask,
} from "@/lib/modules/users/userTasks/actions";
import { isServerActionError } from "@/lib/utils";
import { useTRPC, useTRPCClient } from "@/trpc/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState, type Dispatch } from "react";
import { toast } from "sonner";

const logger = getLogger("useWorkflowOrchestration");

interface UseWorkflowOrchestrationParams {
  state: WorkflowReducerState;
  dispatch: Dispatch<WorkflowAction>;
  isSessionOwner: boolean;
  setIsSessionOwner: (owner: boolean) => void;
  onSessionCleared?: (payload?: RelayedStopWorkflowPayload) => void;
  onOwnershipConflict?: () => void;
}

export function useWorkflowOrchestration({
  state,
  dispatch,
  isSessionOwner,
  setIsSessionOwner,
  onSessionCleared,
  onOwnershipConflict,
}: UseWorkflowOrchestrationParams) {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [queueQueryInput, setQueueQueryInput] = useState(
    getWorkflowDateBoundaries,
  );
  const [queueQueryForSessionId, setQueueQueryForSessionId] = useState<
    string | null
  >(null);

  if (state.sessionId !== null && queueQueryForSessionId !== state.sessionId) {
    setQueueQueryForSessionId(state.sessionId);
    setQueueQueryInput(getWorkflowDateBoundaries());
  }

  const sessionSyncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reclassifyScheduledIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const syncInFlightRef = useRef<Promise<void> | null>(null);
  const stopInFlightRef = useRef<Promise<StopWorkflowResult> | null>(null);
  /** Serializes every owner-side Redis mutation (background sync + stop) so
   * they can never interleave. Each queued task re-reads ownership/session
   * state itself once it actually starts running — after every earlier
   * queued mutation (and any conflict recovery it triggered) has fully
   * settled — instead of trusting a snapshot taken before it was queued. */
  const ownerMutationQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const enqueueOwnerMutation = useCallback(
    <T>(fn: () => Promise<T>): Promise<T> => {
      const run = ownerMutationQueueRef.current.then(fn, fn);
      ownerMutationQueueRef.current = run.then(
        () => undefined,
        () => undefined,
      );
      return run;
    },
    [],
  );
  const rehydrateAfterConflictRef = useRef<() => Promise<boolean>>(
    async () => false,
  );
  const startedAtRef = useRef<string>("");
  /** Redis session version last observed by this tab — sent as
   * `expectedVersion` on the next sync so a stale writer gets rejected
   * instead of silently clobbering a newer write from another owner tab. */
  const sessionVersionRef = useRef<number>(0);
  const pendingSkipTaskIdsRef = useRef(new Set<number>());
  const pendingUnskipTaskIdsRef = useRef(new Set<number>());
  const scheduledContinueExcludeIdRef = useRef<number | null>(null);
  const scheduledPromptShouldAdvanceRef = useRef(false);
  const scheduledResumeTaskIdRef = useRef<number | null>(null);
  const hydrationAttemptedRef = useRef(false);
  const navigationInFlightTaskIdRef = useRef<number | null>(null);
  /** After a silent resume (page refresh or a newly opened CRM tab), do not
   * snap this tab onto the Redis current task's student — that's how a
   * reload or family-referral tab jumped the autodialer. Hold until
   * currentTaskId actually changes (skip / next); ownership claiming and
   * queue polls must not re-open the Opening overlay for the restored task. */
  const suppressOwnerTaskNavigationRef = useRef(false);
  const silentRestoreTaskIdRef = useRef<number | null>(null);
  /** Owner already pushed live state into this tab. A slower Redis hydrate
   * must not overwrite it (new-tab resume vs SYNC_STATE race). */
  const receivedOwnerSyncRef = useRef(false);
  const isCompactViewport = useIsMobile();
  const stateRef = useRef(state);
  const isSessionOwnerRef = useRef(isSessionOwner);
  const onSessionClearedRef = useRef(onSessionCleared);
  const onOwnershipConflictRef = useRef(onOwnershipConflict);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    onSessionClearedRef.current = onSessionCleared;
    onOwnershipConflictRef.current = onOwnershipConflict;
  }, [onOwnershipConflict, onSessionCleared]);

  useEffect(() => {
    isSessionOwnerRef.current = isSessionOwner;
  }, [isSessionOwner]);

  useEffect(() => {
    sessionVersionRef.current = state.sessionVersion;
  }, [state.sessionVersion]);

  const syncDispatch = useCallback(
    (action: WorkflowAction) => {
      stateRef.current = workflowReducer(stateRef.current, action);
      dispatch(action);
    },
    [dispatch],
  );

  useEffect(() => {
    if (state.sessionId === null) {
      return;
    }

    const timer = setTimeout(() => {
      setQueueQueryInput(getWorkflowDateBoundaries());
    }, getWorkflowQueueBoundaryRefreshDelayMs() + 1);

    return () => clearTimeout(timer);
  }, [state.sessionId, queueQueryInput.queueWindowEndUtc]);

  const dispatchQueueUpdate = useCallback(
    (result: {
      queue: WorkflowQueueItem[];
      scheduledStack: WorkflowQueueItem[];
    }) => {
      syncDispatch({
        type: "SET_QUEUE",
        payload: {
          queue: result.queue,
          scheduledStack: result.scheduledStack,
          pendingSkipTaskIds: Array.from(pendingSkipTaskIdsRef.current),
          pendingUnskipTaskIds: Array.from(pendingUnskipTaskIdsRef.current),
        },
      });
    },
    [syncDispatch],
  );

  const buildSessionPayload =
    useCallback((): WorkflowRedisSessionInput | null => {
      const current = stateRef.current;
      if (!current.sessionId) {
        return null;
      }

      return {
        sessionId: current.sessionId,
        startedAt: startedAtRef.current,
        state: current.state,
        queuePosition: current.queuePosition,
        currentTaskId: current.currentTaskId,
        skippedTaskIds: Array.from(current.skippedSet),
        autoAdvance: WORKFLOW_REDIS_LEGACY_AUTO_ADVANCE,
        awaitingFirstCall: current.awaitingFirstCall,
        completedCount: current.completedCount,
        dailyGoal: WORKFLOW_REDIS_LEGACY_DAILY_GOAL,
        queueSize: current.queue.length,
        skippedCount: current.skippedSet.size,
        sessionBaselineCompletedCount: current.sessionBaselineCompletedCount,
        sessionBaselineSkippedCount: current.sessionBaselineSkippedCount,
        convertedCount: current.convertedCount,
        sessionSkippedCount: current.sessionSkippedCount,
        lastCallStartedAt:
          current.lastCallStartedAt ?? startedAtRef.current ?? undefined,
      };
    }, []);

  const clearLocalWorkflow = useCallback(() => {
    if (sessionSyncIntervalRef.current) {
      clearInterval(sessionSyncIntervalRef.current);
      sessionSyncIntervalRef.current = null;
    }
    if (reclassifyScheduledIntervalRef.current) {
      clearInterval(reclassifyScheduledIntervalRef.current);
      reclassifyScheduledIntervalRef.current = null;
    }
    if (inactivityIntervalRef.current) {
      clearInterval(inactivityIntervalRef.current);
      inactivityIntervalRef.current = null;
    }

    syncDispatch({ type: "STOP_WORKFLOW" });
    startedAtRef.current = "";
    sessionVersionRef.current = 0;
    pendingSkipTaskIdsRef.current.clear();
    pendingUnskipTaskIdsRef.current.clear();
    scheduledResumeTaskIdRef.current = null;
    scheduledPromptShouldAdvanceRef.current = false;
    scheduledContinueExcludeIdRef.current = null;
    suppressOwnerTaskNavigationRef.current = false;
    silentRestoreTaskIdRef.current = null;
    receivedOwnerSyncRef.current = false;
    navigationInFlightTaskIdRef.current = null;
    setIsSessionOwner(false);
  }, [setIsSessionOwner, syncDispatch]);

  const syncSessionToRedis = useCallback(async () => {
    if (!isSessionOwnerRef.current) {
      return;
    }
    if (syncInFlightRef.current) {
      return syncInFlightRef.current;
    }

    const runSync = async () => {
      // Re-read ownership now that this task is actually running — an
      // earlier-queued mutation (a stop, or this same sync's own conflict
      // recovery) may have demoted this tab before this one got its turn.
      // Matches the re-check runStop does for the same reason: trusting a
      // pre-queue snapshot here would let a demoted tab overwrite the
      // winning owner's session with stale local state.
      if (!isSessionOwnerRef.current) {
        return;
      }
      const session = buildSessionPayload();
      if (!session) {
        return;
      }

      try {
        const result = await syncWorkflowSession({
          session,
          expectedVersion: sessionVersionRef.current,
        });
        if (isServerActionError(result)) {
          if (result.error === WORKFLOW_SYNC_SAVE_CONFLICT_ERROR) {
            // A version conflict proves this tab's snapshot is stale. Yield
            // before any later effect can retry it, then replace local UI
            // state from the authoritative Redis-backed resume response.
            isSessionOwnerRef.current = false;
            setIsSessionOwner(false);
            logger.warn(
              { error: result.error },
              "Workflow session sync conflict — yielding and rehydrating",
            );
            const rehydrated = await rehydrateAfterConflictRef.current();
            if (!rehydrated) {
              clearLocalWorkflow();
            } else {
              onOwnershipConflictRef.current?.();
            }
            return;
          }

          logger.warn(
            { error: result.error },
            "Failed to sync workflow session",
          );
          return;
        }

        if (!isSessionOwnerRef.current) {
          return;
        }

        sessionVersionRef.current = result.version;
        syncDispatch({
          type: "SET_SESSION_VERSION",
          payload: { version: result.version },
        });
      } catch (error) {
        logger.warn({ error }, "Failed to sync workflow session");
      }
    };

    syncInFlightRef.current = enqueueOwnerMutation(runSync).finally(() => {
      syncInFlightRef.current = null;
    });

    return syncInFlightRef.current;
  }, [
    buildSessionPayload,
    clearLocalWorkflow,
    enqueueOwnerMutation,
    setIsSessionOwner,
    syncDispatch,
  ]);

  const startSessionSyncInterval = useCallback(() => {
    if (sessionSyncIntervalRef.current) {
      clearInterval(sessionSyncIntervalRef.current);
    }
    sessionSyncIntervalRef.current = setInterval(() => {
      void syncSessionToRedis();
    }, WORKFLOW_SESSION_SYNC_INTERVAL_MS);
  }, [syncSessionToRedis]);

  const startReclassifyScheduledInterval = useCallback(() => {
    if (reclassifyScheduledIntervalRef.current) {
      clearInterval(reclassifyScheduledIntervalRef.current);
    }
    reclassifyScheduledIntervalRef.current = setInterval(() => {
      const current = stateRef.current;
      if (current.scheduledStack.length === 0) {
        return;
      }
      const now = new Date();
      const { queue, scheduledStack } = resplitWorkflowQueueClientSide(
        current.queue,
        current.scheduledStack,
        now,
      );
      if (scheduledStack.length !== current.scheduledStack.length) {
        syncDispatch({
          type: "RECLASSIFY_EXPIRED_SCHEDULED",
          payload: { queue, scheduledStack },
        });
      }
    }, 60_000);
  }, [syncDispatch]);

  const navigateToWorkflowTaskMessages = useCallback(
    async (taskId: number | null | undefined) => {
      if (!taskId) {
        return;
      }
      if (navigationInFlightTaskIdRef.current === taskId) {
        return;
      }
      navigationInFlightTaskIdRef.current = taskId;

      try {
        const queueOrScheduled = [
          ...stateRef.current.queue,
          ...stateRef.current.scheduledStack,
        ];
        let studentId = getWorkflowQueueItemStudentId(queueOrScheduled, taskId);
        if (studentId == null) {
          const detail = await queryClient.fetchQuery(
            trpc.callWorkflow.getTaskDetail.queryOptions({ taskId }),
          );
          studentId = detail.studentId;
        }

        if (!studentId) {
          logger.warn({ taskId }, "Workflow task has no linked student");
          navigationInFlightTaskIdRef.current = null;
          return;
        }

        const path = getWorkflowTaskStudentMessagesPath(studentId);
        const currentPath =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        if (currentPath === path) {
          navigationInFlightTaskIdRef.current = null;
          // Pathname can update before the profile RSC commits. Leave
          // pending navigation set so the overlay stays up until the
          // layout's rendered studentId matches.
          return;
        }

        // Off the 3-min next-student interval: no-op if still fresh, refetch if stale.
        router.prefetch(path);

        const queueItem = queueOrScheduled.find((task) => task.id === taskId);
        syncDispatch({
          type: "SET_PENDING_TASK_NAVIGATION",
          payload: {
            navigation: {
              taskId,
              studentId,
              clientName: queueItem?.clientName ?? "next family",
            },
          },
        });
        skipNextWorkflowBeforeUnload();
        router.push(path);
      } catch (error) {
        navigationInFlightTaskIdRef.current = null;
        syncDispatch({
          type: "SET_PENDING_TASK_NAVIGATION",
          payload: { navigation: null },
        });
        logger.warn(
          { error, taskId },
          "Failed to navigate to workflow task messages",
        );
      }
    },
    [queryClient, router, trpc, syncDispatch],
  );

  // Keep the CRM tab on the current queue task's student profile when the
  // session starts and whenever the active task changes. Secondary tabs skip
  // this so they can stay on whatever page the admin already had open.
  useEffect(() => {
    if (state.sessionId === null) {
      navigationInFlightTaskIdRef.current = null;
      return;
    }
    if (!isSessionOwner) {
      return;
    }

    const current = stateRef.current;
    // Stay on this tab's URL after refresh / new-tab hydrate. Do not consume
    // the hold on ownership-claim or queue-position updates — those used to
    // re-open the Opening overlay for the restored task, which then stuck.
    if (
      shouldHoldSilentRestoreNavigation({
        suppressSilentRestoreNavigation: suppressOwnerTaskNavigationRef.current,
        restoredTaskId: silentRestoreTaskIdRef.current,
        currentTaskId: current.currentTaskId,
      })
    ) {
      return;
    }
    suppressOwnerTaskNavigationRef.current = false;
    silentRestoreTaskIdRef.current = null;

    const taskId = resolveActiveWorkflowTaskId({
      currentTaskId: current.currentTaskId,
      queue: current.queue,
      queuePosition: current.queuePosition,
    });
    if (taskId == null) {
      return;
    }

    const studentId = getWorkflowQueueItemStudentId(
      [...current.queue, ...current.scheduledStack],
      taskId,
    );

    if (studentId == null) {
      return;
    }

    if (getStudentIdFromAppPath(window.location.pathname) === studentId) {
      return;
    }

    void navigateToWorkflowTaskMessages(taskId);
  }, [
    state.sessionId,
    state.currentTaskId,
    isSessionOwner,
    navigateToWorkflowTaskMessages,
  ]);

  const clearPendingTaskNavigation = useCallback(() => {
    navigationInFlightTaskIdRef.current = null;
    if (stateRef.current.pendingTaskNavigation) {
      syncDispatch({
        type: "SET_PENDING_TASK_NAVIGATION",
        payload: { navigation: null },
      });
    }
  }, [syncDispatch]);

  const resolveWorkflowNavigationTaskId = useCallback(
    (
      queue: WorkflowQueueItem[],
      skippedTaskIds: number[],
      currentTaskId: number | null,
      queuePosition: number | null = null,
    ): number | null => {
      const activeTaskId = resolveActiveWorkflowTaskId({
        currentTaskId,
        queue,
        queuePosition,
      });
      if (activeTaskId != null) {
        return activeTaskId;
      }

      const skippedSet = new Set(skippedTaskIds);
      return (
        findNextNonSkippedQueueTask(queue, skippedSet, null, null)?.id ?? null
      );
    },
    [],
  );

  const applyResumedWorkflow = useCallback(
    (
      result: WorkflowResumeResult,
      options?: {
        silent?: boolean;
        collapsePanel?: boolean;
        stayOnCurrentPage?: boolean;
      },
    ) => {
      const stayOnCurrentPage = options?.stayOnCurrentPage === true;
      const preserveRestoredTask = options?.silent === true;

      // A new CRM tab may already be following the owner via SYNC_STATE.
      // Don't let a slower Redis resume overwrite that (or skip ahead).
      if (
        stayOnCurrentPage &&
        receivedOwnerSyncRef.current &&
        !isSessionOwnerRef.current
      ) {
        if (options?.collapsePanel === true) {
          syncDispatch({
            type: "SET_PANEL_VISIBILITY",
            payload: { visible: false },
          });
        }
        return;
      }

      startedAtRef.current = result.startedAt;
      sessionVersionRef.current = result.version;
      setIsSessionOwner(false);

      const currentTaskId = resolveRestoredWorkflowCurrentTaskId({
        restoredCurrentTaskId: result.currentTaskId,
        queue: result.queue,
        scheduledStack: result.scheduledStack,
        skippedTaskIds: result.skippedTaskIds,
        queuePosition: result.queuePosition,
        preserveRestoredTask,
      });

      if (stayOnCurrentPage) {
        suppressOwnerTaskNavigationRef.current = true;
        silentRestoreTaskIdRef.current = currentTaskId;
      }

      syncDispatch({
        type: "RESTORE_WORKFLOW",
        payload: {
          sessionId: result.sessionId,
          marbleAdminId: result.marbleAdminId,
          queue: result.queue,
          scheduledStack: result.scheduledStack,
          completedCount: result.completedCount,
          state: result.restoredState,
          queuePosition: result.queuePosition,
          currentTaskId,
          skippedTaskIds: result.skippedTaskIds,
          awaitingFirstCall: result.awaitingFirstCall,
          sessionStartedAt: new Date(result.startedAt),
          sessionBaselineCompletedCount: result.sessionBaselineCompletedCount,
          sessionBaselineSkippedCount: result.sessionBaselineSkippedCount,
          convertedCount: result.convertedCount,
          sessionSkippedCount: result.sessionSkippedCount,
          isPanelVisible: options?.collapsePanel === true ? false : true,
          lastCallStartedAt: result.lastCallStartedAt ?? result.startedAt,
          version: result.version,
        },
      });

      if (!options?.silent) {
        toast.success(
          `Workflow resumed — ${result.queue.length} tasks in queue`,
        );

        const taskId = resolveWorkflowNavigationTaskId(
          result.queue,
          result.skippedTaskIds,
          currentTaskId,
          result.queuePosition,
        );
        void navigateToWorkflowTaskMessages(taskId);
      }
    },
    [
      syncDispatch,
      navigateToWorkflowTaskMessages,
      resolveWorkflowNavigationTaskId,
      setIsSessionOwner,
    ],
  );

  const tryResumeStoredWorkflow = useCallback(
    async (options?: {
      silent?: boolean;
      collapsePanel?: boolean;
      stayOnCurrentPage?: boolean;
    }) => {
      if (isCompactWorkflowViewport()) {
        return false;
      }

      const boundaries = getWorkflowDateBoundaries();
      const result = await resumeWorkflowSessionAction(
        boundaries.queueWindowStartUtc,
        boundaries.queueWindowEndUtc,
      );

      if (isServerActionError(result)) {
        return false;
      }

      applyResumedWorkflow(result, options);
      return true;
    },
    [applyResumedWorkflow],
  );

  useEffect(() => {
    rehydrateAfterConflictRef.current = () =>
      tryResumeStoredWorkflow({
        silent: true,
        collapsePanel: !stateRef.current.isPanelVisible,
      });
  }, [tryResumeStoredWorkflow]);

  const startWorkflow = useCallback(async (): Promise<boolean> => {
    if (isCompactWorkflowViewport()) {
      toast.error("Autodialer workflow is only available on desktop.");
      return false;
    }

    syncDispatch({ type: "SET_LOADING", payload: { loading: true } });
    syncDispatch({ type: "SET_ERROR", payload: { error: null } });

    try {
      const boundaries = getWorkflowDateBoundaries();

      const resumed = await tryResumeStoredWorkflow();
      if (resumed) {
        syncDispatch({ type: "SET_LOADING", payload: { loading: false } });
        return true;
      }

      const result = await startWorkflowAction(
        boundaries.queueWindowStartUtc,
        boundaries.queueWindowEndUtc,
      );

      if (isServerActionError(result)) {
        if (result.error === WORKFLOW_ALREADY_RUNNING_ERROR) {
          const recovered = await tryResumeStoredWorkflow();
          syncDispatch({ type: "SET_LOADING", payload: { loading: false } });
          if (recovered) {
            return true;
          }
        }
        syncDispatch({ type: "SET_ERROR", payload: { error: result.error } });
        toast.error(`Could not start workflow: ${result.error}`);
        syncDispatch({ type: "SET_LOADING", payload: { loading: false } });
        return false;
      }

      startedAtRef.current = result.startedAt;
      sessionVersionRef.current = result.version;
      setIsSessionOwner(true);

      syncDispatch({
        type: "START_WORKFLOW",
        payload: {
          sessionId: result.sessionId,
          marbleAdminId: result.marbleAdminId,
          queue: result.queue,
          scheduledStack: result.scheduledStack,
          completedCount: result.completedCount,
          sessionStartedAt: new Date(result.startedAt),
          version: result.version,
        },
      });

      const skippedTaskIds = getSkippedTaskIdsFromQueue([
        ...result.queue,
        ...result.scheduledStack,
      ]);
      const { queuePosition, currentTaskId } = getInitialWorkflowQueueCursor(
        result.queue,
      );

      toast.success(
        `Workflow started — ${result.queue.length} tasks in queue.`,
      );

      const dueScheduled = findDueScheduledCall(
        result.scheduledStack,
        new Set(skippedTaskIds),
      );
      if (dueScheduled) {
        scheduledPromptShouldAdvanceRef.current = false;
        syncDispatch({
          type: "SET_SCHEDULED_PROMPT",
          payload: { task: dueScheduled },
        });
      }

      const firstTaskId = resolveWorkflowNavigationTaskId(
        result.queue,
        skippedTaskIds,
        currentTaskId,
        queuePosition,
      );
      void navigateToWorkflowTaskMessages(firstTaskId);

      syncDispatch({ type: "SET_LOADING", payload: { loading: false } });
      return true;
    } catch (error) {
      logger.error({ error }, "Unexpected error starting workflow");
      syncDispatch({
        type: "SET_ERROR",
        payload: { error: "Unexpected error. Try again." },
      });
      toast.error("Unexpected error starting workflow");
      syncDispatch({ type: "SET_LOADING", payload: { loading: false } });
      return false;
    }
  }, [
    syncDispatch,
    navigateToWorkflowTaskMessages,
    resolveWorkflowNavigationTaskId,
    setIsSessionOwner,
    tryResumeStoredWorkflow,
  ]);

  const buildSummaryFromRef = useCallback(() => {
    const current = stateRef.current;
    return buildWorkflowSessionSummaryStats({
      completedCount: current.completedCount,
      convertedCount: current.convertedCount,
      sessionSkippedCount: current.sessionSkippedCount,
      sessionStartedAt: current.sessionStartedAt,
      startedAtIso: startedAtRef.current,
      sessionBaselineCompletedCount: current.sessionBaselineCompletedCount,
      tasksCompletedCount: current.sessionCompletedTaskIds.size,
    });
  }, []);

  const fetchServerSummary =
    useCallback(async (): Promise<WorkflowSessionSummaryStats | null> => {
      if (!stateRef.current.sessionId) {
        return null;
      }

      const result = await fetchWorkflowSessionSummary(
        stateRef.current.sessionSkippedCount,
      );

      if (isServerActionError(result)) {
        logger.warn(
          { error: result.error },
          "Failed to fetch server workflow session summary",
        );
        return null;
      }

      return result;
    }, []);

  const resolveSessionSummaryStats = useCallback(
    async (
      clientSummary: WorkflowSessionSummaryStats,
    ): Promise<WorkflowSessionSummaryStats> => {
      const serverSummary = await fetchServerSummary();
      return serverSummary ?? clientSummary;
    },
    [fetchServerSummary],
  );

  const recordCompletedCallInSession = useCallback(
    (options?: {
      isConverted?: boolean;
      completedTaskIds?: number[];
      convertedDelta?: number;
      countsAsDial?: boolean;
    }): WorkflowQueueItem[] => {
      if (options?.countsAsDial !== false) {
        syncDispatch({ type: "INCREMENT_COMPLETED_COUNT" });
      }
      const convertedDelta =
        options?.convertedDelta ?? (options?.isConverted ? 1 : 0);
      if (convertedDelta > 0) {
        for (let i = 0; i < convertedDelta; i++) {
          syncDispatch({ type: "INCREMENT_CONVERTED" });
        }
      } else if (convertedDelta < 0) {
        for (let i = 0; i < -convertedDelta; i++) {
          syncDispatch({ type: "DECREMENT_CONVERTED" });
        }
      }
      // Optimistically remove the completed task from the queue/scheduled-stack
      // display so it disappears immediately without waiting for the 30-min poll.
      const currentTaskId = stateRef.current.currentTaskId;
      const taskIds =
        options?.completedTaskIds ??
        (currentTaskId !== null ? [currentTaskId] : []);
      const removedScheduledTasks: WorkflowQueueItem[] = [];
      for (const taskId of taskIds) {
        const scheduledTask = stateRef.current.scheduledStack.find(
          (t) => t.id === taskId,
        );
        if (scheduledTask) {
          removedScheduledTasks.push(scheduledTask);
          syncDispatch({
            type: "REMOVE_FROM_SCHEDULED_STACK",
            payload: { taskId },
          });
          syncDispatch({
            type: "MARK_TASK_COMPLETED",
            payload: { taskId },
          });
        } else if (stateRef.current.queue.some((t) => t.id === taskId)) {
          syncDispatch({
            type: "MARK_TASK_COMPLETED",
            payload: { taskId },
          });
        }
      }
      return removedScheduledTasks;
    },
    [syncDispatch],
  );

  const revertCompletedCallFromSession = useCallback(
    (options?: {
      wasConverted?: boolean;
      convertedDelta?: number;
      completedTaskIds?: number[];
      skippedTaskIds?: number[];
      countsAsDial?: boolean;
      removedScheduledTasks?: WorkflowQueueItem[];
    }) => {
      if (options?.countsAsDial !== false) {
        syncDispatch({ type: "DECREMENT_COMPLETED_COUNT" });
      }
      const convertedDelta =
        options?.convertedDelta ?? (options?.wasConverted ? 1 : 0);
      for (let i = 0; i < convertedDelta; i++) {
        syncDispatch({ type: "DECREMENT_CONVERTED" });
      }
      for (const taskId of options?.completedTaskIds ?? []) {
        syncDispatch({ type: "UNMARK_TASK_COMPLETED", payload: { taskId } });
      }
      for (const task of options?.removedScheduledTasks ?? []) {
        syncDispatch({
          type: "RESTORE_TO_SCHEDULED_STACK",
          payload: { task },
        });
      }
      for (const taskId of options?.skippedTaskIds ?? []) {
        syncDispatch({ type: "UNSKIP_TASK", payload: { taskId } });
      }
      syncDispatch({ type: "SET_CALL_LOG_SAVED", payload: { saved: false } });
    },
    [syncDispatch],
  );

  const stopWorkflow = useCallback(
    async (
      options?: StopWorkflowOptions,
      onStopAccepted?: () => Promise<void>,
    ): Promise<StopWorkflowResult> => {
      if (!stateRef.current.sessionId) {
        return { stopped: false };
      }

      if (stopInFlightRef.current) {
        return stopInFlightRef.current;
      }

      const runStop = async (): Promise<StopWorkflowResult> => {
        // Re-read ownership and session identity now that this task is
        // actually running — every earlier-queued owner mutation (a
        // heartbeat/debounced sync, and any conflict recovery it triggered)
        // has fully settled by this point. One of them may have demoted this
        // tab or replaced the session entirely; stopping on stale beliefs
        // would delete a session a different tab or browser now owns.
        if (!isSessionOwnerRef.current) {
          return { stopped: false };
        }
        const sessionId = stateRef.current.sessionId;
        if (!sessionId) {
          return { stopped: false };
        }

        const expectedVersion = sessionVersionRef.current;

        const clientSummary = buildSummaryFromRef();
        const endReason =
          options?.sessionEndReason === "inactivity_timeout"
            ? "inactivity_timeout"
            : "stopped";

        try {
          const result = await trpcClient.callWorkflow.stop.mutate({
            sessionId,
            expectedVersion,
            adminCallSid: options?.adminCallSid,
            sessionSkippedCount: stateRef.current.sessionSkippedCount,
            reason: endReason,
          });

          const summaryStats = result.sessionSummary ?? clientSummary;

          await onStopAccepted?.();

          clearLocalWorkflow();
          onSessionClearedRef.current?.({ summaryStats });

          return { stopped: true, summaryStats };
        } catch (error) {
          if (isWorkflowStopConflict(error)) {
            logger.warn(
              {
                failureKind: "workflow_stop_conflict",
                sessionId,
                expectedVersion,
              },
              "Workflow stop conflict — yielding and rehydrating",
            );
            await reconcileWorkflowStopConflict({
              yieldOwnership: () => {
                isSessionOwnerRef.current = false;
                setIsSessionOwner(false);
              },
              rehydrate: () => rehydrateAfterConflictRef.current(),
              clearLocalWorkflow,
              resumeElection: () => onOwnershipConflictRef.current?.(),
            });
            return { stopped: false };
          }
          logger.error({ error }, "Error stopping workflow");
          return {
            stopped: false,
            errorMessage:
              error instanceof Error
                ? error.message
                : "Error stopping workflow",
          };
        }
      };

      stopInFlightRef.current = enqueueOwnerMutation(runStop).finally(() => {
        stopInFlightRef.current = null;
      });

      return stopInFlightRef.current;
    },
    [
      buildSummaryFromRef,
      clearLocalWorkflow,
      enqueueOwnerMutation,
      setIsSessionOwner,
      trpcClient,
    ],
  );

  const { data: polledQueue, refetch: refetchWorkflowQueue } = useQuery({
    ...trpc.callWorkflow.getQueueList.queryOptions(queueQueryInput),
    enabled: state.sessionId !== null && isSessionOwner,
    refetchInterval:
      state.sessionId !== null && isSessionOwner
        ? WORKFLOW_QUEUE_POLL_INTERVAL_MS
        : false,
  });

  const skipTaskMutation = useMutation({
    mutationFn: async (taskId: number) => {
      const result = await skipUserTask(taskId);
      if (isServerActionError(result)) {
        throw new Error(result.error);
      }
      return taskId;
    },
    onMutate: (taskId) => {
      pendingSkipTaskIdsRef.current.add(taskId);
      pendingUnskipTaskIdsRef.current.delete(taskId);
      syncDispatch({ type: "SKIP_TASK", payload: { taskId } });
    },
    onSettled: (_data, error, taskId) => {
      pendingSkipTaskIdsRef.current.delete(taskId);
      if (!error) {
        void syncSessionToRedis();
      }
    },
    onError: (error, taskId) => {
      syncDispatch({ type: "UNSKIP_TASK", payload: { taskId } });
      toast.error(error.message || "Failed to skip task");
    },
  });

  const unskipTaskMutation = useMutation({
    mutationFn: async (taskId: number) => {
      const result = await unskipUserTask(taskId);
      if (isServerActionError(result)) {
        throw new Error(result.error);
      }
      return taskId;
    },
    onMutate: (taskId) => {
      pendingUnskipTaskIdsRef.current.add(taskId);
      pendingSkipTaskIdsRef.current.delete(taskId);
      syncDispatch({ type: "UNSKIP_TASK", payload: { taskId } });
    },
    onSettled: (_data, error, taskId) => {
      pendingUnskipTaskIdsRef.current.delete(taskId);
      if (!error) {
        void syncSessionToRedis();
      }
    },
    onError: (error, taskId) => {
      syncDispatch({ type: "SKIP_TASK", payload: { taskId } });
      toast.error(error.message || "Failed to unskip task");
    },
  });

  const skipTask = useCallback(
    (taskId: number) => {
      return skipTaskMutation.mutateAsync(taskId);
    },
    [skipTaskMutation],
  );

  const unskipTask = useCallback(
    (taskId: number) => {
      return unskipTaskMutation.mutateAsync(taskId);
    },
    [unskipTaskMutation],
  );

  const transitionState = useCallback(
    (newState: CallWorkflowState) => {
      syncDispatch({ type: "TRANSITION_STATE", payload: { state: newState } });
    },
    [syncDispatch],
  );

  /**
   * Confirms a locally-known "next" task is still valid before advancing to
   * it — without refetching the whole queue. A single getTaskDetail call
   * (usually an instant cache hit, since WorkflowCallWorkspace prefetches the
   * next queue position's task detail while the admin is still on the
   * current one) confirms the task still exists, is unchanged, and is
   * actually callable (linked to a student).
   */
  const verifyNextWorkflowTaskDoable = useCallback(
    async (candidate: WorkflowQueueItem): Promise<boolean> => {
      try {
        const detail = await queryClient.fetchQuery(
          trpc.callWorkflow.getTaskDetail.queryOptions({
            taskId: candidate.id,
          }),
        );
        return (
          detail.studentId != null &&
          detail.taskType === candidate.taskType &&
          detail.targetUserId === candidate.targetUserId
        );
      } catch (error) {
        logger.warn(
          { error, taskId: candidate.id },
          "Next workflow task failed doable check",
        );
        return false;
      }
    },
    [queryClient, trpc],
  );

  /**
   * Move to the next non-skipped task, or pause when exhausted.
   *
   * Checks the scheduled stack first: if a held call is due within ±10 minutes,
   * prompt instead of advancing the regular queue. Uses the already-known local
   * queue and a single per-task doable check instead of refetching the whole
   * queue on every advance — a full refetch only runs as a fallback when that
   * check fails, or when the local queue looks exhausted (worth double-checking
   * before pausing the session).
   */
  const moveToNextTaskOrPauseWorkflow = useCallback(
    async (
      afterTaskId: number | null,
      skippedSet: Set<number>,
    ): Promise<boolean> => {
      const afterQueuePosition = stateRef.current.queuePosition;

      let freshQueue = stateRef.current.queue;
      let freshScheduled = stateRef.current.scheduledStack;
      const excludeScheduledId = scheduledContinueExcludeIdRef.current;
      scheduledContinueExcludeIdRef.current = null;

      const dueScheduled = findDueScheduledCall(freshScheduled, skippedSet, {
        excludeTaskId: excludeScheduledId ?? afterTaskId,
      });
      if (dueScheduled) {
        scheduledPromptShouldAdvanceRef.current = true;
        syncDispatch({
          type: "SET_SCHEDULED_PROMPT",
          payload: { task: dueScheduled },
        });
        return true;
      }

      const findResumeTask = (queue: WorkflowQueueItem[]) => {
        return findCallableQueueTaskById(
          queue,
          scheduledResumeTaskIdRef.current,
          skippedSet,
          stateRef.current.completedTaskIds,
        );
      };

      let nextTask =
        findResumeTask(freshQueue) ??
        findNextNonSkippedQueueTask(
          freshQueue,
          skippedSet,
          afterTaskId,
          afterQueuePosition,
          stateRef.current.completedTaskIds,
        );

      const needsFreshQueue =
        nextTask == null || !(await verifyNextWorkflowTaskDoable(nextTask));

      if (needsFreshQueue) {
        try {
          const result = await refetchWorkflowQueue();
          if (result.data) {
            dispatchQueueUpdate(result.data);
            freshQueue = result.data.queue;
            freshScheduled = result.data.scheduledStack;
          }
        } catch (error) {
          logger.warn({ error }, "Failed to refresh queue while advancing");
        }

        const dueAfterRefresh = findDueScheduledCall(
          freshScheduled,
          skippedSet,
          { excludeTaskId: excludeScheduledId ?? afterTaskId },
        );
        if (dueAfterRefresh) {
          scheduledPromptShouldAdvanceRef.current = true;
          syncDispatch({
            type: "SET_SCHEDULED_PROMPT",
            payload: { task: dueAfterRefresh },
          });
          return true;
        }

        nextTask =
          findResumeTask(freshQueue) ??
          findNextNonSkippedQueueTask(
            freshQueue,
            skippedSet,
            afterTaskId,
            afterQueuePosition,
            stateRef.current.completedTaskIds,
          );
      }

      if (nextTask) {
        scheduledResumeTaskIdRef.current = null;
        syncDispatch({
          type: "SET_QUEUE_POSITION",
          payload: {
            position: findQueueTaskIndex(freshQueue, nextTask.id),
          },
        });
        syncDispatch({
          type: "SET_CURRENT_TASK_ID",
          payload: { taskId: nextTask.id },
        });
        if (stateRef.current.state !== "COUNTDOWN") {
          syncDispatch({
            type: "TRANSITION_STATE",
            payload: { state: "COUNTDOWN" },
          });
        }
        void navigateToWorkflowTaskMessages(nextTask.id);
        // Persist the new currentTaskId right away — this is the main path
        // that advances the queue after a logged call, and the debounced
        // sync effect alone leaves a window where a refresh restores stale
        // (already-completed) task/queue-position state.
        void syncSessionToRedis();
        return true;
      }

      const hasHeldScheduled = freshScheduled.some(
        (task) => !skippedSet.has(task.id),
      );
      if (hasHeldScheduled) {
        syncDispatch({
          type: "SET_QUEUE_POSITION",
          payload: { position: null },
        });
        syncDispatch({
          type: "SET_CURRENT_TASK_ID",
          payload: { taskId: null },
        });
        if (stateRef.current.state !== "COUNTDOWN") {
          syncDispatch({
            type: "TRANSITION_STATE",
            payload: { state: "COUNTDOWN" },
          });
        }
        void syncSessionToRedis();
        return true;
      }

      scheduledResumeTaskIdRef.current = null;

      syncDispatch({
        type: "SET_QUEUE_POSITION",
        payload: { position: null },
      });
      syncDispatch({
        type: "SET_CURRENT_TASK_ID",
        payload: { taskId: null },
      });

      try {
        const result = await pauseWorkflowAction();
        if (isServerActionError(result)) {
          toast.error(`Error pausing workflow: ${result.error}`);
          syncDispatch({ type: "PAUSE_WORKFLOW" });
          void syncSessionToRedis();
          return false;
        }
      } catch (error) {
        logger.error({ error }, "Error pausing workflow after queue exhausted");
      }

      syncDispatch({ type: "PAUSE_WORKFLOW" });
      void syncSessionToRedis();
      const summaryStats = await resolveSessionSummaryStats(
        buildSummaryFromRef(),
      );
      trackDataDogAction(
        "autodialer:workflow:queue_exhausted",
        workflowSessionSummaryStatsToAnalyticsContext(summaryStats),
      );
      showWorkflowSessionSummaryToast(summaryStats, "queue_exhausted");
      return false;
    },
    [
      syncDispatch,
      syncSessionToRedis,
      dispatchQueueUpdate,
      navigateToWorkflowTaskMessages,
      refetchWorkflowQueue,
      verifyNextWorkflowTaskDoable,
      buildSummaryFromRef,
      resolveSessionSummaryStats,
    ],
  );

  /**
   * Advance to the next non-skipped task after a completed log.
   * Refreshes the queue from the server first; pauses when nothing remains.
   */
  const advanceQueue = useCallback(async (): Promise<boolean> => {
    const current = stateRef.current;
    const { skippedSet } = current;
    const activeTaskId = resolveActiveWorkflowTaskId({
      currentTaskId: current.currentTaskId,
      queue: current.queue,
      queuePosition: current.queuePosition,
    });

    return moveToNextTaskOrPauseWorkflow(activeTaskId, skippedSet);
  }, [moveToNextTaskOrPauseWorkflow]);

  /**
   * Move to the next queue task after a mistake cancel (draft log deleted).
   * Does not complete the task or increment the session completed count.
   */
  const advanceQueueAfterMistakeCancel = useCallback(async () => {
    const current = stateRef.current;
    const { skippedSet } = current;
    const activeTaskId = resolveActiveWorkflowTaskId({
      currentTaskId: current.currentTaskId,
      queue: current.queue,
      queuePosition: current.queuePosition,
    });

    await moveToNextTaskOrPauseWorkflow(activeTaskId, skippedSet);
  }, [moveToNextTaskOrPauseWorkflow]);

  const skipCurrentAndAdvance = useCallback(
    async (taskId: number) => {
      const skipResult = await _persistSkipTaskWithRetries(taskId);
      if (!skipResult.success) {
        toast.error(skipResult.error);
        return;
      }

      syncDispatch({ type: "SKIP_TASK", payload: { taskId } });
      void syncSessionToRedis();

      const skippedSet = new Set([...stateRef.current.skippedSet, taskId]);

      await moveToNextTaskOrPauseWorkflow(taskId, skippedSet);
    },
    [syncDispatch, moveToNextTaskOrPauseWorkflow, syncSessionToRedis],
  );

  const takeScheduledCallNow = useCallback(
    async (taskId: number) => {
      const current = stateRef.current;
      const task =
        current.scheduledStack.find((item) => item.id === taskId) ??
        current.scheduledPromptTask;
      if (!task || current.skippedSet.has(task.id)) {
        return;
      }

      const advancing = scheduledPromptShouldAdvanceRef.current;
      scheduledPromptShouldAdvanceRef.current = false;

      const nextRegular = advancing
        ? findNextNonSkippedQueueTask(
            current.queue,
            current.skippedSet,
            current.currentTaskId,
            current.queuePosition,
            current.completedTaskIds,
          )
        : (findCallableQueueTaskById(
            current.queue,
            scheduledResumeTaskIdRef.current,
            current.skippedSet,
            current.completedTaskIds,
          ) ??
          current.queue.find(
            (item) =>
              item.id === current.currentTaskId &&
              !current.skippedSet.has(item.id) &&
              !current.completedTaskIds.has(item.id),
          ) ??
          findNextNonSkippedQueueTask(
            current.queue,
            current.skippedSet,
            null,
            null,
            current.completedTaskIds,
          ));

      scheduledResumeTaskIdRef.current =
        nextRegular?.id ?? scheduledResumeTaskIdRef.current;

      syncDispatch({
        type: "SET_SCHEDULED_PROMPT",
        payload: { task: null },
      });
      syncDispatch({
        type: "SET_QUEUE_POSITION",
        payload: {
          position: findQueueTaskIndex(current.queue, nextRegular?.id ?? null),
        },
      });
      syncDispatch({
        type: "SET_CURRENT_TASK_ID",
        payload: { taskId: task.id },
      });
      if (current.state !== "COUNTDOWN") {
        syncDispatch({
          type: "TRANSITION_STATE",
          payload: { state: "COUNTDOWN" },
        });
      }
      void navigateToWorkflowTaskMessages(task.id);
      void syncSessionToRedis();
    },
    [syncDispatch, navigateToWorkflowTaskMessages, syncSessionToRedis],
  );

  const continuePastScheduledPrompt = useCallback(async () => {
    const prompted = stateRef.current.scheduledPromptTask;
    const shouldAdvance = scheduledPromptShouldAdvanceRef.current;
    scheduledPromptShouldAdvanceRef.current = false;
    syncDispatch({
      type: "SET_SCHEDULED_PROMPT",
      payload: { task: null },
    });
    if (!shouldAdvance) {
      return;
    }
    if (prompted) {
      scheduledContinueExcludeIdRef.current = prompted.id;
    }

    const current = stateRef.current;
    const activeTaskId = resolveActiveWorkflowTaskId({
      currentTaskId: current.currentTaskId,
      queue: current.queue,
      queuePosition: current.queuePosition,
    });
    await moveToNextTaskOrPauseWorkflow(activeTaskId, current.skippedSet);
  }, [syncDispatch, moveToNextTaskOrPauseWorkflow]);

  useEffect(() => {
    if (!state.sessionId || !isSessionOwner) {
      if (sessionSyncIntervalRef.current) {
        clearInterval(sessionSyncIntervalRef.current);
        sessionSyncIntervalRef.current = null;
      }
      if (reclassifyScheduledIntervalRef.current) {
        clearInterval(reclassifyScheduledIntervalRef.current);
        reclassifyScheduledIntervalRef.current = null;
      }
      return;
    }
    startSessionSyncInterval();
    startReclassifyScheduledInterval();
  }, [
    isSessionOwner,
    startReclassifyScheduledInterval,
    startSessionSyncInterval,
    state.sessionId,
  ]);

  useEffect(() => {
    if (!polledQueue) {
      return;
    }

    dispatchQueueUpdate(polledQueue);
  }, [polledQueue, dispatchQueueUpdate]);

  useEffect(() => {
    if (hydrationAttemptedRef.current || stateRef.current.sessionId !== null) {
      return;
    }

    hydrationAttemptedRef.current = true;

    if (isCompactWorkflowViewport()) {
      return;
    }

    void tryResumeStoredWorkflow({
      silent: true,
      collapsePanel: true,
      stayOnCurrentPage: true,
    });
  }, [tryResumeStoredWorkflow]);

  useEffect(() => {
    if (!isCompactViewport || state.sessionId === null || !isSessionOwner) {
      return;
    }

    void stopWorkflow();
  }, [isCompactViewport, isSessionOwner, state.sessionId, stopWorkflow]);

  useEffect(() => {
    if (!state.sessionId || !isSessionOwner) {
      return;
    }

    const timer = setTimeout(() => {
      void syncSessionToRedis();
    }, 150);

    return () => clearTimeout(timer);
  }, [
    isSessionOwner,
    state.sessionId,
    state.state,
    state.queuePosition,
    state.currentTaskId,
    state.skippedSet,
    state.awaitingFirstCall,
    state.completedCount,
    state.convertedCount,
    state.sessionBaselineCompletedCount,
    state.sessionBaselineSkippedCount,
    state.sessionSkippedCount,
    state.queue.length,
    state.lastCallStartedAt,
    syncSessionToRedis,
  ]);

  useEffect(() => {
    if (!state.sessionId || !isSessionOwner) {
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
      }
      return;
    }

    inactivityIntervalRef.current = setInterval(() => {
      const current = stateRef.current;
      if (
        current.state === "CONNECTING" ||
        current.state === "ACTIVE_CALL" ||
        current.state === "POST_CALL_LOGGING"
      ) {
        return;
      }
      const lastCallStartedAt = current.lastCallStartedAt;
      if (!lastCallStartedAt) {
        return;
      }
      const idleMs = Date.now() - Date.parse(lastCallStartedAt);
      if (idleMs > WORKFLOW_INACTIVITY_TIMEOUT_MS) {
        void stopWorkflow({ sessionEndReason: "inactivity_timeout" });
      }
    }, WORKFLOW_INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
      }
    };
  }, [isSessionOwner, state.sessionId, stopWorkflow]);

  useEffect(() => {
    return () => {
      if (sessionSyncIntervalRef.current) {
        clearInterval(sessionSyncIntervalRef.current);
      }
      if (reclassifyScheduledIntervalRef.current) {
        clearInterval(reclassifyScheduledIntervalRef.current);
      }
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
      }
    };
  }, []);

  const markOwnerSyncReceived = useCallback(() => {
    receivedOwnerSyncRef.current = true;
  }, []);

  return {
    startWorkflow,
    stopWorkflow,
    skipTask,
    unskipTask,
    skipCurrentAndAdvance,
    takeScheduledCallNow,
    continuePastScheduledPrompt,
    transitionState,
    advanceQueue,
    advanceQueueAfterMistakeCancel,
    recordCompletedCallInSession,
    revertCompletedCallFromSession,
    clearPendingTaskNavigation,
    clearLocalWorkflow,
    markOwnerSyncReceived,
  };
}

const SKIP_TASK_RETRY_COUNT = 2;

async function _persistSkipTaskWithRetries(
  taskId: number,
): Promise<{ success: true } | { success: false; error: string }> {
  const maxAttempts = SKIP_TASK_RETRY_COUNT + 1;
  let lastError = "Failed to skip task";

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const result = await skipUserTask(taskId);
    if (!isServerActionError(result)) {
      return { success: true };
    }

    lastError = result.error;
    if (attempt < maxAttempts) {
      logger.warn(
        { taskId, attempt, error: result.error },
        "Retrying skip task",
      );
    }
  }

  return { success: false, error: lastError };
}
